/**
 * <email-signup-form>
 *
 * Progressive-enhancement wrapper around the email signup's native
 * `{% form 'customer' %}`. The form submits with a full-page reload, so there is a
 * short network gap between the click and the new page rendering. This element adds a
 * processing state to the submit button during that gap so the visitor gets immediate
 * feedback and can't double-submit.
 *
 * The form works without JavaScript — this only layers on the loading affordance.
 */
class EmailSignupForm extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
    // Clear the state if the page is restored from the back/forward cache.
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  get #button() {
    return this.querySelector('.email-signup__button');
  }

  /**
   * `submit` only fires once the browser's native constraint validation has passed
   * (valid, non-empty email), so a real submission — and navigation — is under way.
   */
  #onSubmit = () => {
    const button = this.#button;
    if (!button) return;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
  };

  #onPageShow = (event) => {
    if (!event.persisted) return;
    const button = this.#button;
    if (!button) return;
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
  };
}

if (!customElements.get('email-signup-form')) {
  customElements.define('email-signup-form', EmailSignupForm);
}
